import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  BellIcon,
  Button,
  Card,
  CheckCircleIcon,
  Cluster,
  EmptyState,
  Field,
  InboxIcon,
  Metric,
  Page,
  QueryState,
  SegmentBar,
  Select,
  ShieldIcon,
  Stack,
  StatStrip,
  TextArea,
} from '@integra/ui';
import {
  useApproveReply,
  useConnectorConfigs,
  useEngagementReplies,
  useEngagements,
  useFetchEngagement,
  useProposeReply,
  useRejectReply,
} from '../api/hooks.js';
import type { EngagementItem, EngagementReply } from '../api/types.js';

/**
 * Engage (§20.6) — the inbox and the governed reply. INTEGRA already publishes;
 * this is the other half of the loop: inbound comments/mentions land here, and a
 * reply is never posted to a channel until it is proposed, approved by a human,
 * and written to the append-only audit trail — the same spine as publishing.
 *
 * All data goes through the hooks layer (§21.4); no fetch here.
 */

const KIND_LABEL: Record<string, string> = {
  comment: 'Comment',
  mention: 'Mention',
  direct_message: 'DM',
  review: 'Review',
};

function replyTone(status: EngagementReply['status']): 'neutral' | 'success' | 'warning' | 'error' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'warning';
  if (status === 'failed') return 'error';
  return 'neutral';
}

/** One inbox item, with its reply composer and the governance ledger. */
function EngagementCard({ workspaceId, item }: { workspaceId: string; item: EngagementItem }) {
  const replies = useEngagementReplies(workspaceId, item.id);
  const propose = useProposeReply(workspaceId);
  const approve = useApproveReply(workspaceId);
  const reject = useRejectReply(workspaceId);
  const [draft, setDraft] = useState('');

  const pending = (replies.data?.data ?? []).find((r) => r.status === 'pending');

  const submit = (): void => {
    if (draft.trim() === '') return;
    propose.mutate({ itemId: item.id, draftText: draft }, { onSuccess: () => setDraft('') });
  };

  return (
    <Card>
      <Stack gap="md">
        <Cluster justify="between" align="start" gap="md">
          <div>
            <Cluster gap="sm" align="center">
              <Badge tone="neutral">{KIND_LABEL[item.kind] ?? item.kind}</Badge>
              <span className="text-xs uppercase tracking-wide text-ink-muted">{item.source}</span>
              {item.status === 'replied' && (
                <Badge tone="success" dot>
                  Replied
                </Badge>
              )}
            </Cluster>
            <p className="mt-2 font-heading text-base text-navy-700">{item.authorName}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink-muted">{item.body}</p>
            {item.permalink !== null && (
              <a
                href={item.permalink}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-xs font-semibold text-gold-text hover:underline"
              >
                View on channel →
              </a>
            )}
          </div>
        </Cluster>

        {/* Pending reply → the approval gate. */}
        {pending !== undefined ? (
          <div className="rounded-md border border-edge bg-surface-muted p-3">
            <Cluster justify="between" align="center" gap="sm">
              <Badge tone="warning" dot>
                Reply awaiting approval
              </Badge>
            </Cluster>
            <p className="mt-2 text-sm text-navy-700">{pending.draftText}</p>
            <Cluster gap="sm" align="center" className="mt-3">
              <Button
                size="sm"
                onClick={() => approve.mutate(pending.id)}
                loading={approve.isPending}
              >
                Approve &amp; post
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => reject.mutate({ replyId: pending.id, reason: 'Rejected in review' })}
                loading={reject.isPending}
              >
                Reject
              </Button>
            </Cluster>
            {approve.error instanceof Error && (
              <p className="mt-2 text-xs text-error">{approve.error.message}</p>
            )}
          </div>
        ) : item.status === 'open' ? (
          <Field label="Draft a reply" htmlFor={`reply-${item.id}`}>
            <Stack gap="sm">
              <TextArea
                id={`reply-${item.id}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a reply — it won't post until someone approves it."
                rows={2}
              />
              <Cluster gap="sm" align="center">
                <Button
                  size="sm"
                  onClick={submit}
                  loading={propose.isPending}
                  disabled={draft.trim() === ''}
                >
                  Propose reply
                </Button>
                <span className="text-xs text-ink-muted">
                  Governed: approval + audit before it posts.
                </span>
              </Cluster>
              {propose.error instanceof Error && (
                <p className="text-xs text-error">{propose.error.message}</p>
              )}
            </Stack>
          </Field>
        ) : null}

        {/* Decided replies — the append-only trail. */}
        {(replies.data?.data ?? [])
          .filter((r) => r.status !== 'pending')
          .slice(0, 3)
          .map((r) => (
            <div key={r.id} className="flex items-start gap-2 text-xs text-ink-muted">
              <Badge tone={replyTone(r.status)} dot>
                {r.status}
              </Badge>
              <span className="mt-0.5">{r.draftText}</span>
            </div>
          ))}
      </Stack>
    </Card>
  );
}

export function EngagePage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const inbox = useEngagements(workspaceId);
  const configs = useConnectorConfigs(workspaceId);
  const fetchInbound = useFetchEngagement(workspaceId);
  const [configId, setConfigId] = useState('');

  const items = inbox.data?.data ?? [];
  const connectors = configs.data?.data ?? [];

  const openCount = items.filter((i) => i.status === 'open').length;
  const repliedCount = items.filter((i) => i.status === 'replied').length;
  const dismissedCount = items.filter((i) => i.status === 'dismissed').length;

  return (
    <Page
      title="Engage"
      icon={InboxIcon}
      description="The other half of the loop — inbound comments, mentions and DMs land here; every reply passes the same approval gate and audit trail as a publish."
      stats={
        items.length > 0 ? (
          <StatStrip
            stats={[
              { icon: InboxIcon, label: 'Inbound', value: items.length },
              { icon: BellIcon, label: 'Needs reply', value: openCount, tone: 'accent' },
              { icon: CheckCircleIcon, label: 'Replied', value: repliedCount },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Card title="Pull inbound engagement">
          <Cluster gap="md" align="end">
            <Field label="From channel" htmlFor="engage-source">
              <Select
                id="engage-source"
                value={configId}
                onChange={(e) => setConfigId(e.target.value)}
              >
                <option value="">Select a connected channel…</option>
                {connectors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              onClick={() => fetchInbound.mutate({ connectorConfigId: configId })}
              loading={fetchInbound.isPending}
              disabled={configId === ''}
            >
              Pull inbound
            </Button>
          </Cluster>
          {fetchInbound.data !== undefined && (
            <p className="mt-3 text-sm text-ink-muted">
              Pulled {fetchInbound.data.fetched} item{fetchInbound.data.fetched === 1 ? '' : 's'}.
            </p>
          )}
          {fetchInbound.error instanceof Error && (
            <Alert tone="error" title="Couldn’t pull from that channel">
              {fetchInbound.error.message}
            </Alert>
          )}
        </Card>

        <QueryState loading={inbox.isLoading} error={inbox.error} what="the inbox">
          {inbox.data !== undefined && items.length === 0 ? (
            <EmptyState
              icon={InboxIcon}
              title="Your inbox is clear"
              description="Inbound engagement lands here the moment it arrives. Pull from a connected channel above, or wait for the next sync."
              features={[
                { icon: BellIcon, label: 'Comments, mentions & DMs in one queue' },
                { icon: ShieldIcon, label: 'Every reply passes an approval gate' },
                { icon: CheckCircleIcon, label: 'Append-only audit trail on every reply' },
              ]}
            />
          ) : (
            <Stack gap="md">
              {items.length > 0 && (
                <Card
                  title="Inbox at a glance"
                  subtitle="Every reply passes the same approval gate and audit trail as a publish."
                >
                  <Stack gap="md">
                    <div className="grid grid-cols-3 gap-3">
                      <Metric label="Inbound" value={items.length} />
                      <Metric label="Needs reply" value={openCount} accent={openCount > 0} />
                      <Metric label="Replied" value={repliedCount} />
                    </div>
                    <SegmentBar
                      segments={[
                        { label: 'Open', value: openCount, series: 'accent' },
                        { label: 'Replied', value: repliedCount, series: 'success' },
                        { label: 'Dismissed', value: dismissedCount, series: 'muted' },
                      ]}
                    />
                  </Stack>
                </Card>
              )}
              {items.map((item) => (
                <EngagementCard key={item.id} workspaceId={workspaceId} item={item} />
              ))}
            </Stack>
          )}
        </QueryState>
      </Stack>
    </Page>
  );
}
